import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { ApiError } from '@akillidernek/api-client'
import { Button, ToastProvider } from '@akillidernek/ui'
import { api, bootstrapSession } from './api'
import { markBootstrapped, setAccessToken } from './auth-store'
import { Basvuru } from './routes/basvuru'
import { Destek } from './routes/destek'
import { Giris } from './routes/giris'
import { Uyeligim } from './routes/uyeligim'
import { tenantSlug } from './tenant'
import { useAccessToken, useBootstrapped } from './use-auth'
import { useDernek } from './use-dernek'
import '@akillidernek/ui/tokens.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

type Ekran = 'uyeligim' | 'destek'
type Misafir = 'giris' | 'basvuru'

/**
 * Üye portalı — YES-159, YES-167.
 *
 * Router yok: portal iki ekrandan ibaret ve derinlemesine gezinme yok.
 * Panelin aksine dernek SEÇİLMEZ — hangi derneğin portalındaysan odur
 * (alt alan adı); dernek çözülemezse hiçbir şey gösterilmez, çünkü
 * "hangi derneğe başvuruyorum" sorusunun cevabı olmadan form anlamsız.
 */
function App() {
  const token = useAccessToken()
  const hazir = useBootstrapped()
  const dernek = useDernek()
  const [misafir, setMisafir] = useState<Misafir>('giris')
  const [ekran, setEkran] = useState<Ekran>('uyeligim')
  const [basvuruAlindi, setBasvuruAlindi] = useState(false)
  // Girişli ama bu dernekte kaydı olmayan kullanıcı için başvuru formu.
  const [girisliBasvuru, setGirisliBasvuru] = useState(false)

  if (!tenantSlug) {
    return (
      <Kabuk>
        <p className="text-sm">
          Hangi derneğin portalı olduğu belirlenemedi. Derneğinizin size verdiği bağlantıyı
          kullanın.
        </p>
      </Kabuk>
    )
  }
  if (!hazir || dernek.isLoading) return null
  if (dernek.isError || !dernek.data) {
    return (
      <Kabuk>
        <p className="text-sm">Dernek portalı şu anda açılamıyor. Daha sonra tekrar deneyin.</p>
      </Kabuk>
    )
  }

  const ad = dernek.data.name

  if (basvuruAlindi) {
    return (
      <Kabuk baslik={ad}>
        <div className="flex w-[min(30rem,100%)] flex-col gap-3 text-sm">
          <h2 className="text-lg font-semibold">Başvurunuz alındı</h2>
          <p>
            {ad} yönetim kurulu başvurunuzu değerlendirecek. Sonucu portala giriş yaparak
            görebilirsiniz.
          </p>
          <Button
            onClick={() => {
              setBasvuruAlindi(false)
              setMisafir('giris')
            }}
          >
            Giriş yap
          </Button>
        </div>
      </Kabuk>
    )
  }

  if (!token) {
    return (
      <Kabuk baslik={ad}>
        {misafir === 'giris' ? (
          <Giris onBasvur={() => setMisafir('basvuru')} />
        ) : (
          <Basvuru
            dernek={dernek.data}
            girisli={false}
            onGiris={() => setMisafir('giris')}
            onTamam={() => setBasvuruAlindi(true)}
          />
        )}
      </Kabuk>
    )
  }

  return (
    <div className="min-h-dvh bg-[var(--ad-bg)] text-[var(--ad-text)]">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--ad-border)] bg-[var(--ad-surface)] px-5 py-3">
        <span className="font-semibold">{ad}</span>
        <nav className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant={ekran === 'uyeligim' ? 'primary' : 'ghost'}
            onClick={() => setEkran('uyeligim')}
          >
            Üyeliğim
          </Button>
          <Button
            size="sm"
            variant={ekran === 'destek' ? 'primary' : 'ghost'}
            onClick={() => setEkran('destek')}
          >
            Destek
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void api.POST('/api/v1/auth/logout').catch(() => undefined)
              setAccessToken(null)
              queryClient.clear()
            }}
          >
            Çıkış
          </Button>
        </nav>
      </header>
      <main className="mx-auto w-[min(48rem,100%)] p-5">
        {ekran === 'destek' ? (
          <Destek />
        ) : girisliBasvuru ? (
          <Basvuru
            dernek={dernek.data}
            girisli
            onGiris={() => setGirisliBasvuru(false)}
            onTamam={() => {
              setGirisliBasvuru(false)
              void queryClient.invalidateQueries({ queryKey: ['portal-me'] })
            }}
          />
        ) : (
          <Uyeligim onBasvur={() => setGirisliBasvuru(true)} />
        )}
      </main>
    </div>
  )
}

function Kabuk({ baslik, children }: { baslik?: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-[var(--ad-bg)] p-6 text-[var(--ad-text)]">
      <div className="flex w-full flex-col items-center gap-4">
        {baslik && <h1 className="text-lg font-semibold">{baslik}</h1>}
        {children}
      </div>
    </div>
  )
}

void bootstrapSession().finally(markBootstrapped)

const container = document.getElementById('root')
if (!container) throw new Error('#root bulunamadı')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <App />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)
